/**
 * CalorieBalanceCard.tsx
 *
 * Extracted from HomeScreen.tsx JSX.
 * Expandable calorie balance card with breakdown.
 * Target: src/screens/Home/CalorieBalanceCard.tsx
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCalorieUnitLabel,
  formatLabelWithUnit,
} from '../../utils/unitLabels';
import type { WeeklyInsightResponse } from '../../services/weeklyInsight.service';
import { interpretDeficit } from '../../utils/deficitInterpretation';

interface CalorieBalanceCardProps {
  balanceDirection: string;
  balanceValue: number;
  displayCaloriesEaten: number;
  dailyBurnTotal: number;
  resting: number;
  activityBurn: number;
  foodUnits: any;
  yesterdayDeficit: number | null;
  weeklyInsight?: WeeklyInsightResponse | null;
  daysLogged?: number;
}

export const CalorieBalanceCard: React.FC<CalorieBalanceCardProps> = ({
  balanceDirection,
  balanceValue,
  displayCaloriesEaten,
  dailyBurnTotal,
  resting,
  activityBurn,
  foodUnits,
  yesterdayDeficit,
  weeklyInsight,
  daysLogged = 0,
}) => {
  const navigate = useNavigate();

  // Weekly report icon state
  // not_ready = before Sunday | ready = Sunday, unread | read = opened this week
  const [reportState, setReportState] = useState<'not_ready' | 'ready' | 'read'>('not_ready');

  useEffect(() => {
    const today = new Date();
    const isSunday = today.getDay() === 0;
    if (!isSunday) { setReportState('not_ready'); return; }
    try {
      // ISO week string e.g. '2026-W13'
      const d = new Date(today);
      d.setUTCHours(0, 0, 0, 0);
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
      const isoWeek = `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
      const readAt = localStorage.getItem('coretrak.weeklyReport.readAt');
      setReportState(readAt === isoWeek ? 'read' : 'ready');
    } catch { setReportState('ready'); }
  }, []);

  function handleReportIconClick() {
    try {
      const d = new Date();
      d.setUTCHours(0, 0, 0, 0);
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
      const isoWeek = `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
      localStorage.setItem('coretrak.weeklyReport.readAt', isoWeek);
    } catch { /* best effort */ }
    setReportState('read');
    navigate('/weekly-report');
  }

  return (
    <div className="rounded-[20px]" style={{ background: 'rgba(255,255,255,0.93)', border: '1px solid rgba(225,225,225,0.4)', boxShadow: '0px 4px 22px 0px rgba(0,0,0,0.012)', padding: '13px 20px 14px', position: 'relative', overflow: 'hidden' }}>
      {/* Teal tint bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #007c86, rgba(0,124,134,0.3) 70%, transparent)', borderRadius: '20px 20px 0 0' }} />
      <div className="relative">
        <div style={{ marginBottom: 0, textAlign: 'center' }}>
          <span style={{ fontSize: 12, color: '#a0a0a0', letterSpacing: -0.2, opacity: 0.8 }}>Calories In &amp; Calories Out (CICO)</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 1, justifyContent: 'center' }}>
          <span style={{ fontSize: 36, fontWeight: 700, color: '#007c86', letterSpacing: -0.8, lineHeight: 1.0, fontVariantNumeric: 'tabular-nums' }}>{balanceValue}</span>
          <span style={{ fontSize: 13, fontWeight: 400, color: 'rgba(0,0,0,0.30)' }}>kcal</span>
        </div>

        <>
            <div style={{ height: 0 }} />
            <div style={{ height: 1, background: 'rgba(0,0,0,0.07)' }} />
            <div style={{ height: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '3px 2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, letterSpacing: -0.2, fontWeight: 400, color: '#141517' }}>
                  {formatLabelWithUnit("Resting Calories", getCalorieUnitLabel(foodUnits))}
                </span>
                <span style={{ fontSize: 14, letterSpacing: -0.2, fontWeight: 400, color: '#141517', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(resting)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, letterSpacing: -0.2, fontWeight: 400, color: '#141517' }}>
                  {formatLabelWithUnit("Activity Burn (Exercise + Residual)", getCalorieUnitLabel(foodUnits))}
                </span>
                <span style={{ fontSize: 14, letterSpacing: -0.2, fontWeight: 400, color: '#141517', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(activityBurn)}
                </span>
              </div>
            </div>
            <div style={{ height: 0 }} />
            <div style={{ height: 1, background: 'rgba(0,0,0,0.07)', margin: '0 2px' }} />
            <div style={{ height: 0 }} />
            <div style={{ padding: '2px 2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, letterSpacing: -0.2, fontWeight: 600, color: '#141517' }}>
                  {formatLabelWithUnit("Total Daily Burn", getCalorieUnitLabel(foodUnits))}
                </span>
                <span style={{ fontSize: 14, letterSpacing: -0.2, fontWeight: 600, color: '#141517', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(dailyBurnTotal)}
                </span>
              </div>
            </div>
            <div style={{ height: 0 }} />
            <div style={{ height: 1, background: 'rgba(0,0,0,0.07)', margin: '0 2px' }} />
            <div style={{ height: 0 }} />
            <div style={{ padding: '2px 2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, letterSpacing: -0.2, fontWeight: 400, color: '#141517' }}>
                  {formatLabelWithUnit("Calories consumed", getCalorieUnitLabel(foodUnits))}
                </span>
                <span style={{ fontSize: 14, letterSpacing: -0.2, fontWeight: 400, color: '#141517', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(displayCaloriesEaten)}
                </span>
              </div>
            </div>
            <div style={{ height: 0 }} />
            <div style={{ height: 1, background: 'rgba(0,0,0,0.25)', margin: '0 2px' }} />
            <div style={{ height: 0 }} />
            <div style={{ padding: '2px 8px', margin: '0 -8px', background: 'rgba(0,124,134,0.05)', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, letterSpacing: -0.2, fontWeight: 600, color: '#141517' }}>
                  {(() => {
                    if (balanceDirection === 'Neutral') return 'Daily Kcal';
                    const signed = balanceDirection === 'Deficit' ? balanceValue : -balanceValue;
                    const lbl = interpretDeficit(signed);
                    const noMeals = displayCaloriesEaten === 0;
                    return (
                      <>Daily Kcal · <span style={{ color: noMeals ? '#e8a030' : lbl.colour }}>{noMeals ? 'No meals logged' : lbl.text}</span></>
                    );
                  })()}
                </span>
                <span style={{ fontSize: 14, letterSpacing: -0.2, fontWeight: 600, color: '#141517', fontVariantNumeric: 'tabular-nums' }}>
                  {balanceValue}
                </span>
              </div>
            </div>
                <div style={{ height: 8 }} />
                <div style={{ height: 1, background: 'rgba(0,0,0,0.05)', margin: '0 2px' }} />
                <div style={{ height: 2 }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px' }}>
                  {yesterdayDeficit !== null ? (() => {
                    const yl = interpretDeficit(yesterdayDeficit);
                    return (
                      <>
                        <span style={{ fontSize: 13, letterSpacing: -0.2, fontWeight: 400, color: '#a0a0a0' }}>
                          Yesterday · <span style={{ color: yl.colour, fontWeight: 500 }}>{yl.text}</span>
                        </span>
                        <span style={{ fontSize: 14, letterSpacing: -0.2, fontWeight: 400, color: '#a0a0a0', fontVariantNumeric: 'tabular-nums' }}>
                          {Math.abs(yesterdayDeficit)}
                        </span>
                      </>
                    );
                  })() : (
                    <>
                      <span style={{ fontSize: 13, letterSpacing: -0.2, fontWeight: 400, color: '#a0a0a0' }}>Yesterday</span>
                      <span style={{ fontSize: 14, letterSpacing: -0.2, fontWeight: 400, color: '#a0a0a0', fontVariantNumeric: 'tabular-nums' }}>--</span>
                    </>
                  )}
                </div>

                {/* Feedback area — baseline message or weekly insight */}
                <div style={{ marginTop: 5, borderTop: '1px solid rgba(0,0,0,0.07)', padding: '6px 2px 2px', minHeight: 32, position: 'relative' }}>
                  {daysLogged <= 2 ? (
                    <p style={{ margin: 0, fontSize: 11, color: '#b5b5b5', fontStyle: 'italic', lineHeight: 1.4 }}>
                      Building your baseline — feedback starts after a few days
                    </p>
                  ) : weeklyInsight?.hasData && weeklyInsight.headline ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#141517', letterSpacing: -0.2, lineHeight: 1.4 }}>{weeklyInsight.headline}</span>
                        <span style={{ fontSize: 10, color: '#007c86', flexShrink: 0, marginLeft: 8, paddingTop: 2 }}>This week</span>
                      </div>
                      {weeklyInsight.subtextVisible && weeklyInsight.subtext && (
                        <p style={{ fontSize: 11, color: '#6B7280', margin: '4px 0 0', lineHeight: 1.4 }}>{weeklyInsight.subtext}</p>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 28 }}>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: '#141517', letterSpacing: -0.2, lineHeight: 1.4 }}>
                        Protein looks on track
                      </p>
                      {/* Composed icon cluster: green doc + state mark inset bottom-right */}
                      <button
                        type="button"
                        onClick={handleReportIconClick}
                        aria-label="Weekly report"
                        style={{ flexShrink: 0, marginLeft: 8, padding: 0, background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0, position: 'relative', width: 22, height: 22 }}
                      >
                        {/* Green document icon — fills container */}
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0 }}>
                          <rect x="3" y="2" width="13" height="17" rx="2" stroke="#16A34A" strokeWidth="1.8" fill="none"/>
                          <line x1="7" y1="7" x2="13" y2="7" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round"/>
                          <line x1="7" y1="11" x2="13" y2="11" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round"/>
                          <line x1="7" y1="15" x2="10" y2="15" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        {/* State mark — inset bottom-right */}
                        {reportState === 'not_ready' && (
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="white" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', bottom: 0, right: 0 }}>
                            <circle cx="6" cy="6" r="6" fill="white"/>
                            <rect x="1.5" y="1.5" width="9" height="9" rx="2" stroke="#E24B4A" strokeWidth="1.2" fill="none"/>
                            <line x1="3.5" y1="3.5" x2="8.5" y2="8.5" stroke="#E24B4A" strokeWidth="1.2" strokeLinecap="round"/>
                            <line x1="8.5" y1="3.5" x2="3.5" y2="8.5" stroke="#E24B4A" strokeWidth="1.2" strokeLinecap="round"/>
                          </svg>
                        )}
                        {reportState === 'ready' && (
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="white" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', bottom: 0, right: 0 }}>
                            <circle cx="6" cy="6" r="6" fill="white"/>
                            <rect x="1.5" y="1.5" width="9" height="9" rx="2" stroke="#007c86" strokeWidth="1.2" fill="none"/>
                            <polyline points="3,6 5,8.5 9,3.5" stroke="#007c86" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        {reportState === 'read' && (
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="white" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', bottom: 0, right: 0 }}>
                            <circle cx="6" cy="6" r="6" fill="white"/>
                            <rect x="1.5" y="1.5" width="9" height="9" rx="2" stroke="#C4C4C4" strokeWidth="1.2" fill="none"/>
                            <polyline points="3,6 5,8.5 9,3.5" stroke="#C4C4C4" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  )}
                </div>
        </>
      </div>
    </div>
  );
};
